import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import { Alert } from '@/components/Alert/Alert';
import { createColumnHelper, DataTable, type ColumnDef } from '@/components/DataTable/DataTable';
import {
  usePositionsCatalog,
  useStaffProfiles,
  useUnitsCatalog,
  type StaffProfile,
} from '@/features/members';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import styles from './StaffProfileList.module.css';

const columnHelper = createColumnHelper<StaffProfile>();

const ALL = '__all__';

export function StaffProfileList() {
  const profiles = useStaffProfiles();
  const positionsCatalog = usePositionsCatalog();
  const unitsCatalog = useUnitsCatalog();

  const [positionFilter, setPositionFilter] = useState<string>(ALL);
  const [unitFilter, setUnitFilter] = useState<string>(ALL);

  const items = useMemo<StaffProfile[]>(() => profiles.data?.items ?? [], [profiles.data]);

  // Lookups from id → name for resolved chips.
  const positionsById = useMemo(() => {
    const map = new Map<string, string>();
    (positionsCatalog.data?.items ?? []).forEach((p) => map.set(p.id, p.name));
    return map;
  }, [positionsCatalog.data]);

  const unitsById = useMemo(() => {
    const map = new Map<string, string>();
    (unitsCatalog.data?.items ?? []).forEach((u) => map.set(u.id, u.name));
    return map;
  }, [unitsCatalog.data]);

  // Filter options derived from the Staff Profiles that are actually
  // referenced — keeps the dropdown small and relevant.
  const positionOptions = useMemo(() => {
    const referenced = new Set<string>();
    items.forEach((p) => p.defaultPositionIds.forEach((id) => referenced.add(id)));
    return Array.from(referenced)
      .map((id) => ({ id, name: positionsById.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, positionsById]);

  const unitOptions = useMemo(() => {
    const referenced = new Set<string>();
    items.forEach((p) => p.defaultUnitIds.forEach((id) => referenced.add(id)));
    return Array.from(referenced)
      .map((id) => ({ id, name: unitsById.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, unitsById]);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (positionFilter !== ALL && !p.defaultPositionIds.includes(positionFilter)) return false;
      if (unitFilter !== ALL && !p.defaultUnitIds.includes(unitFilter)) return false;
      return true;
    });
  }, [items, positionFilter, unitFilter]);

  const columns: ColumnDef<StaffProfile, unknown>[] = [
    columnHelper.accessor((p) => formatPersonName(p), {
      id: 'name',
      header: t('Name'),
      cell: (info) => {
        const profile = info.row.original;
        const name = formatPersonName(profile);
        return (
          <Link
            to={`/library/member-profiles/${encodeURIComponent(profile.id)}`}
            className={styles.nameCell}
          >
            <Avatar name={name} size="sm" kind="ai" />
            <span className={styles.name}>{name}</span>
          </Link>
        );
      },
    }),
    columnHelper.accessor((p) => p.description ?? '', {
      id: 'description',
      header: t('Description'),
      cell: (info) => {
        const desc = info.row.original.description;
        return desc ? <span className={styles.desc}>{desc}</span> : null;
      },
      enableSorting: false,
    }),
    columnHelper.accessor(
      (p) =>
        p.defaultUnitIds
          .map((id) => unitsById.get(id) ?? '')
          .filter(Boolean)
          .sort()
          .join(', '),
      {
        id: 'unit',
        header: t('Department'),
        cell: (info) => {
          const profile = info.row.original;
          const names = profile.defaultUnitIds
            .map((id) => ({ id, name: unitsById.get(id) ?? '' }))
            .filter((u) => u.name)
            .sort((a, b) => a.name.localeCompare(b.name));
          if (names.length === 0) return null;
          return (
            <div className={styles.chipRow}>
              {names.map((u) => (
                <Chip key={u.id} variant="neutral" size="sm">
                  {u.name}
                </Chip>
              ))}
            </div>
          );
        },
      },
    ),
    columnHelper.accessor(
      (p) =>
        p.defaultPositionIds
          .map((id) => positionsById.get(id) ?? '')
          .filter(Boolean)
          .sort()
          .join(', '),
      {
        id: 'position',
        header: t('Position'),
        cell: (info) => {
          const profile = info.row.original;
          const names = profile.defaultPositionIds
            .map((id) => ({ id, name: positionsById.get(id) ?? '' }))
            .filter((p) => p.name)
            .sort((a, b) => a.name.localeCompare(b.name));
          if (names.length === 0) return null;
          return (
            <div className={styles.chipRow}>
              {names.map((p) => (
                <Chip key={p.id} variant="neutral" size="sm">
                  {p.name}
                </Chip>
              ))}
            </div>
          );
        },
      },
    ),
  ];

  if (profiles.isError) {
    return <Alert variant="error">{t("Couldn't load Staff Profiles.")}</Alert>;
  }

  return (
    <DataTable
      data={filtered}
      columns={columns}
      ariaLabel={t('Staff Profiles')}
      defaultSort={{ id: 'name' }}
      isLoading={profiles.isLoading}
      emptyState={
        items.length === 0
          ? t('No Staff Profiles available.')
          : t('No Staff Profiles match these filters.')
      }
      searchPlaceholder={t('Search Staff Profiles…')}
      searchAccessor={(row) => `${formatPersonName(row)} ${row.description ?? ''}`}
      filterChips={
        <>
          <SelectFilter
            label={t('Department')}
            value={unitFilter}
            onChange={setUnitFilter}
            options={unitOptions}
          />
          <SelectFilter
            label={t('Position')}
            value={positionFilter}
            onChange={setPositionFilter}
            options={positionOptions}
          />
        </>
      }
    />
  );
}

interface SelectFilterProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly { id: string; name: string }[];
}

function SelectFilter({ label, value, onChange, options }: SelectFilterProps) {
  return (
    <label className={styles.selectFilter}>
      <span className={styles.selectLabel}>{label}</span>
      <select className={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value={ALL}>{t('All')}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}
